import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { MODULE_ORDER, MODULE_SCHEMAS } from "../lib/moduleSchemas";
import {
  LayoutDashboard, Users, Phone, Landmark, TrendingUp, Shield, CreditCard,
  Home, Lock, TrendingDown, FileText, Receipt, Briefcase, IndianRupee,
  RefreshCw, KeyRound, ClipboardCheck, UsersRound, LifeBuoy, BarChart3,
  Upload, Settings as SettingsIcon, LogOut, Menu, X, Vault, ShieldAlert,
  CalendarCheck2, Link2,
} from "lucide-react";
import { useState } from "react";
import { Button } from "./ui/button";
import QuickSearch from "./QuickSearch";
import QuickAdd from "./QuickAdd";

const ICON_MAP = {
  Users, Phone, Landmark, TrendingUp, Shield, CreditCard, Home, Lock,
  TrendingDown, FileText, Receipt, Briefcase, IndianRupee, RefreshCw,
  KeyRound, ClipboardCheck,
};

function SidebarLink({ to, icon: Icon, label, testid, onClick, count }) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      data-testid={testid}
      className={({ isActive }) =>
        `group flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors
         ${isActive
           ? "bg-slate-900 text-white"
           : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"}`
      }
    >
      <Icon className="h-4 w-4 shrink-0" strokeWidth={1.75} aria-hidden="true" />
      <span className="flex-1 truncate">{label}</span>
      {count != null && count > 0 && (
        <span className="text-[10px] font-medium tabular-nums px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 group-[.active]:bg-white/20 group-[.active]:text-white">
          {count}
        </span>
      )}
    </NavLink>
  );
}

export default function Layout({ children, counts = {} }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const nav = (close) => (
    <nav className="flex flex-col gap-6 p-4">
      <div className="flex items-center gap-2 px-2">
        <div className="h-9 w-9 rounded-md bg-slate-900 flex items-center justify-center">
          <Vault className="h-5 w-5 text-white" strokeWidth={1.8} />
        </div>
        <div>
          <div className="font-heading font-semibold text-slate-900 leading-tight">Family Vault</div>
          <div className="label-overline">Emergency Continuity</div>
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <div className="label-overline px-3 mb-1">Overview</div>
        <SidebarLink to="/" icon={LayoutDashboard} label="Dashboard"
          testid="nav-dashboard" onClick={close} />
        <SidebarLink to="/nominees" icon={UsersRound} label="Nominee Tracker"
          testid="nav-nominees" onClick={close} />
        <SidebarLink to="/emergency-plan" icon={LifeBuoy} label="Emergency Plan"
          testid="nav-emergency-plan" onClick={close} />
        <SidebarLink to="/emergency-access" icon={ShieldAlert} label="Emergency Access"
          testid="nav-emergency-access" onClick={close} />
        <SidebarLink to="/vault" icon={Vault} label="Document Vault"
          testid="nav-vault" onClick={close} />
        <SidebarLink to="/review" icon={CalendarCheck2} label="Yearly Review"
          testid="nav-yearly-review" onClick={close} />
        <SidebarLink to="/shares" icon={Link2} label="Share Links"
          testid="nav-shares" onClick={close} />
      </div>

      <div className="flex flex-col gap-1">
        <div className="label-overline px-3 mb-1">Modules</div>
        {MODULE_ORDER.map((slug) => {
          const s = MODULE_SCHEMAS[slug];
          const Icon = ICON_MAP[s.icon] || FileText;
          return (
            <SidebarLink
              key={slug}
              to={`/m/${slug}`}
              icon={Icon}
              label={s.label}
              testid={`nav-${slug}`}
              onClick={close}
              count={counts[slug]}
            />
          );
        })}
      </div>

      <div className="flex flex-col gap-1">
        <div className="label-overline px-3 mb-1">Tools</div>
        <SidebarLink to="/import" icon={Upload} label="Import Data"
          testid="nav-import" onClick={close} />
        <SidebarLink to="/reports" icon={BarChart3} label="Reports & Exports"
          testid="nav-reports" onClick={close} />
        <SidebarLink to="/settings" icon={SettingsIcon} label="Settings"
          testid="nav-settings" onClick={close} />
      </div>
    </nav>
  );

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Desktop sidebar */}
      <aside className="hidden lg:block w-72 shrink-0 border-r border-slate-200 bg-white sticky top-0 h-screen overflow-y-auto">
        {nav()}
        <div className="px-4 pb-4 mt-4 border-t border-slate-200 pt-4">
          <div className="px-2">
            <div className="text-sm font-medium text-slate-900 truncate">{user?.full_name}</div>
            <div className="text-xs text-slate-500 truncate">{user?.email}</div>
          </div>
          <Button
            variant="outline"
            className="w-full mt-3 justify-start"
            onClick={handleLogout}
            data-testid="nav-logout-button"
          >
            <LogOut className="h-4 w-4 mr-2" strokeWidth={1.75} /> Sign out
          </Button>
        </div>
      </aside>

      {/* Mobile sidebar */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" role="dialog">
          <div className="absolute inset-0 bg-slate-900/60" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-80 max-w-[85%] bg-white overflow-y-auto">
            <div className="flex items-center justify-end p-2">
              <button
                onClick={() => setMobileOpen(false)}
                className="p-2 rounded-md hover:bg-slate-100"
                data-testid="mobile-nav-close-button"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            {nav(() => setMobileOpen(false))}
            <div className="px-4 pb-8 mt-4 border-t border-slate-200 pt-4">
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={handleLogout}
                data-testid="mobile-nav-logout-button"
              >
                <LogOut className="h-4 w-4 mr-2" /> Sign out
              </Button>
            </div>
          </aside>
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        {/* Top bar */}
        <header className="sticky top-0 z-30 sticky-glass">
          <div className="flex items-center justify-between gap-3 px-4 lg:px-8 h-14">
            <div className="flex items-center gap-2">
              <button
                className="lg:hidden p-2 -ml-2 rounded-md hover:bg-slate-100"
                onClick={() => setMobileOpen(true)}
                data-testid="mobile-nav-open-button"
              >
                <Menu className="h-5 w-5" />
              </button>
              <div className="lg:hidden flex items-center gap-2">
                <div className="h-7 w-7 rounded bg-slate-900 flex items-center justify-center">
                  <Vault className="h-4 w-4 text-white" strokeWidth={1.8} />
                </div>
                <span className="font-heading font-semibold text-slate-900">Family Vault</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <QuickSearch />
              <QuickAdd />
              <Link
                to="/emergency-plan"
                className="hidden md:inline-flex text-xs font-medium text-slate-600 hover:text-slate-900 px-3 py-1.5 rounded-md hover:bg-slate-100"
                data-testid="header-emergency-plan-link"
              >
                Emergency Plan
              </Link>
              <span className="text-xs text-slate-500 hidden lg:inline">
                Role · <span className="font-medium text-slate-700 capitalize">{user?.role || "owner"}</span>
              </span>
            </div>
          </div>
        </header>
        <main className="flex-1 px-4 lg:px-8 py-6 lg:py-8">{children}</main>
      </div>
    </div>
  );
}
